/**

 * 관제 영상 URL: 개발(stream_url) vs 실운영(게이트웨이 playback_url)

 */



import { API_ORIGIN } from '../config';



export type CctvStreamSource = {

  id?: number;

  pool_id?: number;

  stream_url?: string;

  url?: string;

  playback_url?: string;

  /** 관제 WebRTC(WHEP). HLS playback과 별도 */
  webrtc_url?: string;

};



export type PlaybackConfig = {

  use_remote_playback: boolean;

  public_api_base_url?: string;

  hls_public_read?: boolean;

  hls_requires_token?: boolean;

  /** 중앙 go2rtc(WHEP/WHIP) 기동 여부 — false면 관제 WebRTC 생략·HLS 사용 */
  central_go2rtc_available?: boolean;

  /** WHIP으로 중앙에 등록된 스트림명 (예: p20_c72) — WebRTC는 이 목록에 있을 때만 */
  central_go2rtc_streams?: string[];

};



function isLoopbackHostname(hostname: string): boolean {

  return hostname === 'localhost' || hostname === '127.0.0.1';

}



function isLoopbackMediaUrl(raw: string): boolean {

  try {

    return isLoopbackHostname(new URL(raw, API_ORIGIN).hostname);

  } catch {

    return false;

  }

}



function isLanClientPage(): boolean {

  if (typeof window === 'undefined') return false;

  const h = window.location.hostname;

  return Boolean(h && !isLoopbackHostname(h));

}



function isDevCraProxyPort(): boolean {

  if (typeof window === 'undefined') return false;

  const port = window.location.port;

  return port === '3500' || port === '3000';

}



/** 관제·CCTV 탭 공통: 재생 URL 선택 */

export function resolveCctvStreamUrl(

  cctv: CctvStreamSource,

  useRemotePlayback: boolean,

  poolSiteGatewayEnabled: boolean

): string | undefined {

  const legacy = (cctv.stream_url || cctv.url || '').trim();

  const remote = (cctv.playback_url || '').trim();

  // 게이트웨이 OFF: stream_url만 사용 (잔존 playback_url 무시)
  if (!poolSiteGatewayEnabled) {

    return legacy || undefined;

  }



  // 서버 목업 HLS(localhost:8500 등) — stream_url 우선 (go2rtc playback 보다 앞)
  if (legacy.toLowerCase().includes('.m3u8') && isLoopbackMediaUrl(legacy)) {

    return legacy;

  }



  if (useRemotePlayback && poolSiteGatewayEnabled && remote) {

    return remote;

  }

  if (

    isLanClientPage() &&

    legacy.toLowerCase().includes('.m3u8') &&

    !isLoopbackMediaUrl(legacy)

  ) {

    return legacy;

  }

  if (

    process.env.NODE_ENV === 'development' &&

    !useRemotePlayback &&

    legacy.toLowerCase().includes('.m3u8') &&

    remote.includes('/api/gateway/hls/')

  ) {

    return legacy;

  }

  // 게이트웨이 ON 일 때만 playback HLS 우선. OFF 이면 stream_url(HLS/http 포함)로 재생.
  if (poolSiteGatewayEnabled && remote && remote.toLowerCase().includes('.m3u8')) {

    return remote;

  }

  return legacy || remote || undefined;

}



/** dev(3500/3000): 게이트웨이 HLS → 페이지 origin(CRA /api 프록시, 폰 CORS 회피) */

export function rewriteGatewayHlsViaDevProxy(url: string): string {

  const trimmed = (url || '').trim();

  if (!trimmed || typeof window === 'undefined') return trimmed;

  if (process.env.NODE_ENV !== 'development') return trimmed;

  if (!isDevCraProxyPort()) return trimmed;



  const pageOrigin = window.location.origin.replace(/\/+$/, '');



  try {

    const parsed = new URL(trimmed, API_ORIGIN);

    const path = parsed.pathname || '';

    if (!path.includes('/api/gateway/hls/')) {

      return trimmed;

    }

    return `${pageOrigin}${path}${parsed.search}${parsed.hash}`;

  } catch {

    return trimmed;

  }

}



/** 폰(LAN): PC localhost HLS → 백엔드 프록시 (동일 origin 3500 /api) */

/** 서버 목업 HLS(127.0.0.1:8500 등) → hls-proxy (관제·원격 브라우저) */
function rewriteServerLoopbackHlsViaProxy(

  url: string,

  poolId?: number,

  cctvId?: number

): string {

  if (!poolId || poolId <= 0 || !cctvId || cctvId <= 0) return url;

  const trimmed = (url || '').trim();

  if (!trimmed.toLowerCase().includes('.m3u8')) return url;



  try {

    const parsed = new URL(trimmed, API_ORIGIN);

    if (parsed.pathname.includes('/api/cctv/hls-proxy/')) return url;

    if (!isLoopbackHostname(parsed.hostname || '')) return url;

    // 게이트웨이 HLS·go2rtc 는 중앙에 이미 있다. hls-proxy(stream_url)로 바꾸면 RTSP가 거절된다.
    if (
      parsed.pathname.includes('/api/gateway/hls/') ||
      parsed.pathname.includes('/go2rtc/')
    ) {
      return url;
    }

  } catch {

    return url;

  }



  const pageOrigin =

    typeof window !== 'undefined'

      ? window.location.origin.replace(/\/+$/, '')

      : API_ORIGIN.replace(/\/+$/, '');

  return `${pageOrigin}/api/cctv/hls-proxy/${poolId}/${cctvId}/index.m3u8`;

}



function rewriteLanStreamViaBackendProxy(

  url: string,

  poolId?: number,

  cctvId?: number

): string {

  // 미리보기용 id(-1/0) 또는 미저장 CCTV 는 프록시하지 않고 원본 URL 재생
  if (!isLanClientPage() || !poolId || poolId <= 0 || !cctvId || cctvId <= 0) return url;

  const trimmed = (url || '').trim();

  if (!trimmed.toLowerCase().includes('.m3u8')) return url;



  try {

    const parsed = new URL(trimmed, API_ORIGIN);

    if (parsed.pathname.includes('/api/gateway/hls/')) return url;

    if (parsed.pathname.includes('/api/cctv/hls-proxy/')) return url;

    // go2rtc·중앙 playback — 공인 URL 직접 재생 (hls-proxy 는 loopback HLS 전용)
    if (parsed.pathname.includes('/go2rtc/')) return url;

    const host = (parsed.hostname || '').toLowerCase();

    if (host && !isLoopbackHostname(host)) return url;

  } catch {

    return url;

  }



  const pageOrigin = window.location.origin.replace(/\/+$/, '');

  return `${pageOrigin}/api/cctv/hls-proxy/${poolId}/${cctvId}/index.m3u8`;

}



/** loopback 미디어 URL의 /api 경로는 dev LAN에서 페이지 포트(3500)로 */

export function rewriteLoopbackMediaUrlForLanAccess(

  url: string,

  options?: { pageHostname?: string; apiOrigin?: string }

): string {

  const trimmed = (url || '').trim();

  if (!trimmed) return trimmed;



  const pageHostname =

    options?.pageHostname ??

    (typeof window !== 'undefined' ? window.location.hostname : '');



  if (!pageHostname || isLoopbackHostname(pageHostname)) {

    return trimmed;

  }



  const apiOrigin = (options?.apiOrigin ?? API_ORIGIN).replace(/\/+$/, '');



  try {

    const parsed = new URL(trimmed, apiOrigin);

    if (!isLoopbackHostname(parsed.hostname)) {

      return parsed.href;

    }

    parsed.hostname = pageHostname;

    const mediaPath = parsed.pathname || '';
    const usePageOrigin =
      mediaPath.startsWith('/api/') || mediaPath.startsWith('/go2rtc/');
    if (
      usePageOrigin &&
      typeof window !== 'undefined' &&
      window.location
    ) {
      const proto = String(window.location.protocol || '').replace(':', '');
      if (proto) parsed.protocol = proto;
      parsed.port = window.location.port || '';
    }

    return parsed.toString();

  } catch {

    return trimmed;

  }

}



/** 팝업(about:blank) 등에서 상대 경로 HLS가 깨지지 않도록 절대 URL로 변환 */

export function absolutizeMediaUrl(

  url: string,

  apiOrigin: string = API_ORIGIN,

  ids?: { poolId?: number; cctvId?: number }

): string {

  const trimmed = (url || '').trim();

  if (!trimmed) return '';

  // rtsp/rtsps 등은 브라우저·스냅샷용 원본 유지 (origin 붙이면 URL 파괴)
  if (/^(rtsp|rtsps):\/\//i.test(trimmed)) {
    return trimmed;
  }

  const loopbackProxied = rewriteServerLoopbackHlsViaProxy(
    trimmed,
    ids?.poolId,
    ids?.cctvId
  );
  if (loopbackProxied.includes('/api/cctv/hls-proxy/')) {
    return loopbackProxied;
  }

  let absolute: string;

  if (/^https?:\/\//i.test(trimmed)) {

    absolute = rewriteLoopbackMediaUrlForLanAccess(trimmed, { apiOrigin });

  } else if (trimmed.startsWith('//')) {

    const protocol =

      typeof window !== 'undefined' && window.location?.protocol

        ? window.location.protocol

        : 'http:';

    absolute = rewriteLoopbackMediaUrlForLanAccess(`${protocol}${trimmed}`, {

      apiOrigin,

    });

  } else {

    const origin = apiOrigin.replace(/\/+$/, '');

    absolute = trimmed.startsWith('/')

      ? `${origin}${trimmed}`

      : `${origin}/${trimmed}`;

    absolute = rewriteLoopbackMediaUrlForLanAccess(absolute, { apiOrigin });

  }

  const viaGateway = rewriteGatewayHlsViaDevProxy(absolute);

  return rewriteCentralPathToPageOrigin(

    rewriteLanStreamViaBackendProxy(

      viaGateway,

      ids?.poolId,

      ids?.cctvId

    )

  );

}



/** 게이트웨이 HLS·go2rtc 는 지금 연 관제 서버에서 받는다. DB에 오타 호스트가 있어도 페이지 주소로 맞춘다. */
function rewriteCentralPathToPageOrigin(url: string): string {
  const trimmed = (url || '').trim();
  if (!trimmed || typeof window === 'undefined' || !window.location?.hostname) {
    return trimmed;
  }
  if (isLoopbackHostname(window.location.hostname)) return trimmed;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    const path = parsed.pathname || '';
    if (!path.includes('/api/gateway/hls/') && !path.includes('/go2rtc/')) {
      return trimmed;
    }
    const proto = String(window.location.protocol || '').replace(':', '');
    if (proto) parsed.protocol = proto;
    parsed.hostname = window.location.hostname;
    parsed.port = window.location.port || '';
    return parsed.toString();
  } catch {
    return trimmed;
  }
}



export function resolveAbsoluteCctvStreamUrl(

  cctv: CctvStreamSource,

  useRemotePlayback: boolean,

  poolSiteGatewayEnabled: boolean

): string {

  const resolved = resolveCctvStreamUrl(cctv, useRemotePlayback, poolSiteGatewayEnabled);

  if (!resolved) return '';

  const poolId = cctv.pool_id;

  const cctvId =

    typeof cctv.id === 'number' ? cctv.id : undefined;

  return absolutizeMediaUrl(resolved, API_ORIGIN, { poolId, cctvId });

}



export function pickCctvPlayUrl(

  cctv: CctvStreamSource,

  useRemotePlayback: boolean,

  poolSiteGatewayEnabled: boolean

): string {

  const absolute = resolveAbsoluteCctvStreamUrl(

    cctv,

    useRemotePlayback,

    poolSiteGatewayEnabled

  );

  if (absolute) return absolute;

  const legacy = (cctv.stream_url || cctv.url || '').trim();

  if (!legacy) return '';

  const poolId = cctv.pool_id;

  const cctvId = typeof cctv.id === 'number' ? cctv.id : undefined;

  return absolutizeMediaUrl(legacy, API_ORIGIN, { poolId, cctvId });

}



/** 관제 라이브: WebRTC 우선, HLS(playback) 폴백. 추론은 playback만 사용(서버). */

export type CctvLivePlayback = {

  mode: 'webrtc' | 'hls' | 'none';

  webrtcUrl: string;

  hlsUrl: string;

};



/** 중앙 go2rtc WHEP — DB webrtc_url 없을 때 pool/cctv id로 합성 */
export function buildCentralWebrtcWhepUrl(
  poolId: number,
  cctvId: number,
  apiOrigin: string = API_ORIGIN
): string {
  const base = apiOrigin.replace(/\/+$/, '');
  return `${base}/go2rtc/api/webrtc?src=p${poolId}_c${cctvId}`;
}

export function pickCctvLivePlayback(

  cctv: CctvStreamSource,

  useRemotePlayback: boolean,

  poolSiteGatewayEnabled: boolean,

  centralGo2rtcAvailable = false,

  centralGo2rtcStreams: string[] = []

): CctvLivePlayback {

  const hlsUrl = resolveAbsoluteCctvStreamUrl(

    cctv,

    useRemotePlayback,

    poolSiteGatewayEnabled

  );

  const poolId = cctv.pool_id;

  const cctvId = typeof cctv.id === 'number' ? cctv.id : undefined;

  const streamKey =

    poolId && cctvId ? `p${poolId}_c${cctvId}` : '';

  const whipOnCentral =

    Boolean(streamKey) && centralGo2rtcStreams.includes(streamKey);

  let webrtcUrl = (cctv.webrtc_url || '').trim();

  if (!webrtcUrl && whipOnCentral && poolId && cctvId) {

    webrtcUrl = buildCentralWebrtcWhepUrl(poolId, cctvId);

  } else if (webrtcUrl) {

    webrtcUrl = absolutizeMediaUrl(webrtcUrl, API_ORIGIN, { poolId, cctvId });

  }

  // active_only 목록에 없어도 webrtc_url 이 있으면 WebRTC 유지.
  // (목록 순단 → HLS 전환이 관제 끊김·흑화면의 주원인)
  if (webrtcUrl && poolSiteGatewayEnabled && centralGo2rtcAvailable) {

    return { mode: 'webrtc', webrtcUrl, hlsUrl: hlsUrl || '' };

  }

  if (hlsUrl) {

    return { mode: 'hls', webrtcUrl: webrtcUrl || '', hlsUrl };

  }

  return { mode: 'none', webrtcUrl: '', hlsUrl: '' };

}


